import { assign, setup } from 'xstate';
import type { ApolloClient } from '@apollo/client';
import type { QuickSearchResult } from '@frameio/graphql-client/generated';
import type { GetQuickSearchResultsV2QueryVariables } from '../gql/generated/GetQuickSearchResultsV2';
import type { QuickSearchBaseContext } from './types';
import {
  fetchLexicalQueryActor,
  fetchNlpQueryActor,
  sharedGuards,
} from './utils';

const multiGuards = {
  ...sharedGuards,
  hasInitialNodes: ({ context }: { context: QuickSearchMultiContext }) =>
    (context.lexicalComplete && context.lexicalNodes.length > 0) ||
    (context.nlpComplete && context.nlpNodes.length > 0),
  canViewAll: ({ context }: { context: QuickSearchMultiContext }) =>
    context.lexicalComplete && context.lexicalNodes.length > 0,
};

export type QuickSearchMultiEvents =
  | {
      type: 'SEARCH';
      requestVariables: GetQuickSearchResultsV2QueryVariables;
      apolloClient?: ApolloClient<unknown>;
    }
  | {
      type: 'RESET';
    }
  | {
      type: 'VIEW_ALL';
    };

export type QuickSearchMultiContext = QuickSearchBaseContext & {
  nlpNodes: QuickSearchResult[];
  nlpError?: Error;
  nlpComplete: boolean;
  showAllLexicalResults: boolean;
};

export type QuickSearchMultiMachineInput = {
  apolloClient: ApolloClient<unknown>;
  requestVariables: QuickSearchBaseContext['requestVariables'];
  initialLexicalNodes?: QuickSearchResult[]; // For hydration - pre-populate results
  initialNlpNodes?: QuickSearchResult[]; // For hydration - pre-populate NLP results
};

/**
 * Quick Search State Machine - MULTI Mode
 */
export const quickSearchMultiMachine = setup({
  types: {
    context: {} as QuickSearchMultiContext,
    events: {} as QuickSearchMultiEvents,
    input: {} as QuickSearchMultiMachineInput,
  },
  // ==================== GUARDS ====================
  guards: multiGuards,
  // ==================== ACTIONS ====================
  actions: {
    updateSearchParams: assign({
      requestVariables: ({ event }) => {
        if (event.type === 'SEARCH') {
          return event.requestVariables;
        }
        return undefined as never;
      },
      apolloClient: ({ event, context }) => {
        if (event.type === 'SEARCH' && event.apolloClient) {
          return event.apolloClient;
        }
        return context.apolloClient;
      },
      lexicalNodes: ({ event }) => {
        if (event.type === 'SEARCH') {
          return [];
        }
        return undefined as never;
      },
      nlpNodes: ({ event }) => {
        if (event.type === 'SEARCH') {
          return [];
        }
        return undefined as never;
      },
      lexicalComplete: () => false,
      nlpComplete: () => false,
      showAllLexicalResults: () => false,
    }),
    setViewAll: assign({
      showAllLexicalResults: () => true,
    }),
    resetViewAll: assign({
      showAllLexicalResults: () => false,
    }),
    clearResults: assign({
      lexicalNodes: () => [],
      nlpNodes: () => [],
      lexicalError: () => undefined,
      nlpError: () => undefined,
      lexicalComplete: () => false,
      nlpComplete: () => false,
      showAllLexicalResults: () => false,
    }),
    storeLexicalSuccess: assign({
      lexicalNodes: ({ event }) => {
        if ('output' in event) {
          return event.output as QuickSearchResult[];
        }
        return [];
      },
      lexicalError: () => undefined,
      lexicalComplete: () => true,
    }),
    storeLexicalError: assign({
      lexicalNodes: () => [],
      lexicalError: ({ event }) => {
        if ('error' in event) {
          return event.error as Error;
        }
        return undefined;
      },
      lexicalComplete: () => true,
    }),
    storeNlpSuccess: assign({
      nlpNodes: ({ event }) => {
        if ('output' in event) {
          return event.output as QuickSearchResult[];
        }
        return [];
      },
      nlpError: () => undefined,
      nlpComplete: () => true,
    }),
    storeNlpError: assign({
      nlpNodes: () => [],
      nlpError: ({ event }) => {
        if ('error' in event) {
          return event.error as Error;
        }
        return undefined;
      },
      nlpComplete: () => true,
    }),
  },
  // ==================== ACTORS ====================
  actors: {
    fetchLexicalQuery: fetchLexicalQueryActor,
    fetchNlpQuery: fetchNlpQueryActor,
  },
}).createMachine({
  id: 'quickSearchMulti',
  initial: 'initializing',
  context: ({ input }: { input: QuickSearchMultiMachineInput }) => ({
    requestVariables: input.requestVariables,
    lexicalNodes: input.initialLexicalNodes ?? [],
    nlpNodes: input.initialNlpNodes ?? [],
    showAllLexicalResults: false,
    lexicalComplete: input.initialLexicalNodes !== undefined,
    apolloClient: input.apolloClient,
    nlpComplete: input.initialNlpNodes !== undefined,
  }),
  on: {
    RESET: {
      target: '.idle',
      actions: ['clearResults'],
    },
  },
  // ==================== STATES ====================
  states: {
    initializing: {
      // Immediately transition to complete if we have cached data, otherwise we transition to our initial idle state
      always: [
        {
          guard: 'hasInitialNodes',
          target: 'complete',
        },
        {
          target: 'idle',
        },
      ],
    },
    idle: {
      on: {
        SEARCH: {
          target: 'searching',
          actions: ['updateSearchParams', 'resetViewAll'],
        },
      },
    },
    searching: {
      initial: 'parallelQueries',
      states: {
        parallelQueries: {
          type: 'parallel',
          states: {
            lexicalQuery: {
              initial: 'lexLoading',
              states: {
                lexLoading: {
                  invoke: {
                    id: 'lexicalQueryActor',
                    src: 'fetchLexicalQuery',
                    timeout: 10000,
                    input: ({ context }) => ({
                      apolloClient:
                        context.apolloClient as ApolloClient<unknown>,
                      requestVariables: context.requestVariables,
                    }),
                    onDone: {
                      target: 'lexSuccess',
                      actions: ['storeLexicalSuccess'],
                    },
                    onError: {
                      target: 'lexError',
                      actions: ['storeLexicalError'],
                    },
                  },
                },
                lexSuccess: {
                  type: 'final',
                },
                lexError: {
                  type: 'final',
                },
              },
            },
            nlpQuery: {
              initial: 'nlpLoading',
              states: {
                nlpLoading: {
                  invoke: {
                    id: 'nlpQueryActor',
                    src: 'fetchNlpQuery',
                    input: ({ context }) => ({
                      apolloClient:
                        context.apolloClient as ApolloClient<unknown>,
                      requestVariables: context.requestVariables,
                    }),
                    onDone: {
                      target: 'nlpSuccess',
                      actions: ['storeNlpSuccess'],
                    },
                    onError: {
                      target: 'nlpError',
                      actions: ['storeNlpError'],
                    },
                  },
                },
                nlpSuccess: {
                  type: 'final',
                },
                nlpError: {
                  type: 'final',
                },
              },
            },
          },
          onDone: {
            target: '#quickSearchMulti.complete',
          },
        },
      },
      on: {
        SEARCH: {
          target: '.parallelQueries',
          actions: ['updateSearchParams', 'resetViewAll'],
        },
      },
    },
    complete: {
      // Final state representing completed queries (success, error, or no results)
      // Outcome is determined by context: lexicalNodes, nlpNodes, lexicalError, nlpError
      on: {
        SEARCH: {
          target: 'searching',
          actions: ['updateSearchParams', 'resetViewAll'],
        },
        VIEW_ALL: {
          guard: 'canViewAll',
          actions: ['setViewAll'],
        },
      },
    },
  },
});
